/**
 * Shared utility for comparing a live MUI DataGrid state against a default state baseline.
 *
 * Centralising this logic prevents the two consumers (useCXUserPrefs and
 * useRegisterDataGridSaveEvents) from drifting out of sync — which was the root
 * cause of Bug 3 (pinnedColumns normalization inconsistency).
 */
export function isDefaultGridState(defaultInitialState: any, currentState: any): boolean {
  if (!defaultInitialState) return false;

  const defaultCols = defaultInitialState.columns?.columnVisibilityModel;
  const currentCols = currentState.columns?.columnVisibilityModel;
  if (JSON.stringify(defaultCols) !== JSON.stringify(currentCols)) return false;

  const normalizePinned = (pinned: any) => ({
    left: pinned?.left || [],
    right: pinned?.right || [],
  });
  const defaultPinned = normalizePinned(defaultInitialState.pinnedColumns);
  const currentPinned = normalizePinned(currentState.pinnedColumns);
  if (JSON.stringify(defaultPinned) !== JSON.stringify(currentPinned)) return false;

  const defaultSort = defaultInitialState.sorting?.sortModel;
  const currentSort = currentState.sorting?.sortModel;
  if (JSON.stringify(defaultSort) !== JSON.stringify(currentSort)) return false;

  const defaultPagination = defaultInitialState.pagination?.paginationModel;
  const currentPagination = currentState.pagination?.paginationModel;
  if (JSON.stringify(defaultPagination) !== JSON.stringify(currentPagination)) return false;

  const defaultDensity = defaultInitialState.density;
  const currentDensity = currentState.density;
  if (defaultDensity !== currentDensity) return false;

  // Filter out MUI-internal columns (e.g. __check__, __row_group_by_columns_group__)
  // before comparing so they don't cause false-positive non-default detections
  const defaultOrderedFields = defaultInitialState.columns?.orderedFields;
  const currentOrderedFields = currentState.columns?.orderedFields;
  const appFieldSet = new Set(defaultOrderedFields ?? []);
  const filteredCurrentOrderedFields = currentOrderedFields?.filter((f: string) => appFieldSet.has(f));
  if (JSON.stringify(defaultOrderedFields) !== JSON.stringify(filteredCurrentOrderedFields)) return false;

  return true;
}
